import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getProject, updateProject, deleteProject, type Project } from "@/lib/projectStore";
import { useAuth } from "../context/AuthContext";
import { EXAMPLE_DIAGRAM } from "@/lib/diagramParser";
import DiagramCanvas from "@/components/DiagramCanvas";
import UserStoryEditor from "@/components/UserStoryEditor";
import AnalysisPanel from "@/components/AnalysisPanel";
import DocumentEditor from "@/components/DocumentEditor";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Trash2, Lightbulb, Download, GitBranch, BarChart3, FileText, PenTool, Users, CalendarDays, Columns3, TrendingUp, ShieldCheck, ClipboardList, Bot, BookOpen, CalendarRange, FlaskConical, TestTube2, Zap, GraduationCap, Sparkles, PackageOpen, BriefcaseBusiness, BookMarked, Wrench, Truck, Table2, Scale, ShieldAlert, NotebookPen, Layers, Target, Brain, Inbox, ScrollText, DollarSign, FolderTree, GitMerge, ArrowRightLeft, Database, MousePointer2, Map, Share2, Lock, Activity } from "lucide-react";
import StakeholderPanel from "@/components/StakeholderPanel";
import TimelinePanel from "@/components/TimelinePanel";
import KanbanBoard from "@/components/KanbanBoard";
import SprintVelocityChart from "@/components/SprintVelocityChart";
import RequirementsPanel from "@/components/RequirementsPanel";
import CompliancePanel from "@/components/CompliancePanel";
import TeamPanel, { type TeamMember } from "@/components/TeamPanel";
import SchedulePanel from "@/components/SchedulePanel";
import AgentLogPanel from "@/components/AgentLogPanel";
import KnowledgeBasePanel from "@/components/KnowledgeBasePanel";
import TestCasesPanel from "@/components/TestCasesPanel";
import TestRunnerPanel from "@/components/TestRunnerPanel";
import ImpactAnalysisPanel from "@/components/ImpactAnalysisPanel";
import CaseStudiesPanel from "@/components/CaseStudiesPanel";
import GeniusLabsPanel from "@/components/GeniusLabsPanel";
import HandoverPanel from "@/components/HandoverPanel";
import JobIntelligencePanel from "@/components/JobIntelligencePanel";
import EvolutionLogPanel from "@/components/EvolutionLogPanel";
import CAPAPanel from "@/components/CAPAPanel";
import SupplierPanel from "@/components/SupplierPanel";
import TraceabilityMatrix from "@/components/TraceabilityMatrix";
import ProjectFlowMap from "@/components/ProjectFlowMap";
import ProjectCrosswalkView from "@/components/ProjectCrosswalkView";
import DecisionLogPanel from "@/components/DecisionLogPanel";
import RiskRegisterPanel from "@/components/RiskRegisterPanel";
import MeetingNotesPanel from "@/components/MeetingNotesPanel";
import SprintPlanningPanel from "@/components/SprintPlanningPanel";
import OKRPanel from "@/components/OKRPanel";
import BrainTrajectoryPanel from "@/components/BrainTrajectoryPanel";
import CatchMeUpPanel from "@/components/CatchMeUpPanel";
import ProjectCharterPanel from "@/components/ProjectCharterPanel";
import BudgetEVMPanel from "@/components/BudgetEVMPanel";
import WBSPanel from "@/components/WBSPanel";
import CriticalPathPanel from "@/components/CriticalPathPanel";
import GapAnalysisPanel from "@/components/GapAnalysisPanel";
import DataDictionaryPanel from "@/components/DataDictionaryPanel";
import UseCasePanel from "@/components/UseCasePanel";
import RoadmapPanel from "@/components/RoadmapPanel";
import ProjectMembersPanel from "@/components/ProjectMembersPanel";
import ActivityFeedPanel from "@/components/ActivityFeedPanel";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useProjectPresence } from "@/hooks/use-project-presence";

type Tab = "diagram" | "stories" | "kanban" | "timeline" | "velocity" | "analysis" | "documents" | "stakeholders" | "requirements" | "compliance" | "agent-log" | "knowledge-base" | "team" | "schedule" | "sharing" | "activity" | "test-cases" | "test-runner" | "impact-analysis" | "case-studies" | "genius-labs" | "handover" | "job-intelligence" | "evolution-log" | "capa" | "suppliers" | "traceability" | "decisions" | "risks" | "meeting-notes" | "sprints" | "okr" | "brain" | "catch-up" | "charter" | "budget-evm" | "wbs" | "critical-path" | "gap-analysis" | "data-dictionary" | "use-cases" | "roadmap";

export default function ProjectEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const [project, setProject] = useState<Project | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [baProjectId, setBaProjectId] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("diagram");
  const [viewMode, setViewMode] = useState<"tabs" | "map" | "crosswalk">("tabs");

  useEffect(() => {
    if (!id) return;
    getProject(id).then(async (p) => {
      if (!p) { navigate("/studio"); return; }
      setProject(p);
      setText(p.diagramText);
      setTeam((p as any).team || []);
      try {
        const base = process.env.REACT_APP_BACKEND_URL;
        const { data } = await axios.get(
          `${base}/api/tools/projects/by-mongo/${id}?name=${encodeURIComponent(p.name)}`,
          { headers: authHeader }
        );
        setBaProjectId(data.id);
      } catch (_) {}
    });
  }, [id, navigate]);

  const refreshProject = useCallback(() => {
    if (!id) return;
    getProject(id).then((p) => { if (p) setProject(p); });
  }, [id]);

  const handleRemoteActivity = useCallback(() => {
    refreshProject();
  }, [refreshProject]);
  const { peers, setActiveTab: setPresenceTab } = useProjectPresence(project?.id, handleRemoteActivity);

  useEffect(() => {
    setPresenceTab(activeTab);
  }, [activeTab, setPresenceTab]);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedUpdate = useCallback(
    (updates: Partial<Omit<Project, "id" | "createdAt">>) => {
      if (!id) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        updateProject(id, updates);
      }, 800);
    },
    [id]
  );

  const saveText = useCallback(
    (newText: string) => {
      setText(newText);
      debouncedUpdate({ diagramText: newText });
    },
    [debouncedUpdate]
  );

  const handleStatusChange = (status: Project["status"]) => {
    if (!id) return;
    updateProject(id, { status }).then((updated) => { if (updated) setProject(updated); });
  };

  const handleDelete = () => {
    if (!id) return;
    deleteProject(id).then(() => navigate("/studio"));
  };

  const handleLoadExample = () => {
    saveText(EXAMPLE_DIAGRAM);
  };

  const handleExportSVG = () => {
    const svgEl = document.querySelector(".diagram-area svg");
    if (!svgEl) return;
    const data = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([data], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${project?.name || "diagram"}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!project) return null;

  const tabGroups: { label: string; tabs: { id: Tab; label: string; icon: React.ComponentType<any> }[] }[] = [
    {
      label: "Project",
      tabs: [
        { id: "diagram", label: "Diagram", icon: PenTool },
        { id: "stories", label: "User Stories", icon: GitBranch },
        { id: "kanban", label: "Board", icon: Columns3 },
        { id: "timeline", label: "Timeline", icon: CalendarDays },
        { id: "velocity", label: "Velocity", icon: TrendingUp },
        { id: "analysis", label: "Analysis Archive", icon: BarChart3 },
        { id: "documents", label: "Documents", icon: FileText },
        { id: "stakeholders", label: "Stakeholders", icon: Users },
        { id: "requirements", label: "Requirements", icon: ClipboardList },
        { id: "compliance", label: "Compliance", icon: ShieldCheck },
        { id: "knowledge-base", label: "Knowledge Base", icon: BookOpen },
        { id: "agent-log", label: "Agent Log", icon: Bot },
        { id: "test-cases", label: "Test Cases", icon: FlaskConical },
        { id: "test-runner", label: "Test Runner", icon: TestTube2 },
        { id: "impact-analysis", label: "Impact Analysis", icon: Zap },
        { id: "case-studies", label: "Case Studies", icon: GraduationCap },
        { id: "handover", label: "Handover", icon: PackageOpen },
        { id: "evolution-log", label: "Evolution Log", icon: BookMarked },
        { id: "traceability", label: "Traceability", icon: Table2 },
        { id: "capa", label: "CAPA", icon: Wrench },
        { id: "suppliers", label: "Suppliers", icon: Truck },
      ],
    },
    {
      label: "Team & Schedule",
      tabs: [
        { id: "team", label: "Team", icon: Users },
        { id: "schedule", label: "Schedule", icon: CalendarRange },
        { id: "sharing", label: "Sharing", icon: Share2 },
        { id: "activity", label: "Activity", icon: Activity },
      ],
    },
    {
      label: "Intelligence",
      tabs: [
        { id: "genius-labs", label: "Genius Labs", icon: Sparkles },
        { id: "job-intelligence", label: "Job Intel", icon: BriefcaseBusiness },
      ],
    },
    {
      label: "Planning",
      tabs: [
        { id: "meeting-notes", label: "Meeting Notes", icon: NotebookPen },
        { id: "decisions", label: "Decision Log", icon: Scale },
        { id: "risks", label: "Risk Register", icon: ShieldAlert },
        { id: "sprints", label: "Sprint Planning", icon: Layers },
        { id: "okr", label: "OKR", icon: Target },
      ],
    },
    {
      label: "Reflect",
      tabs: [
        { id: "brain",    label: "Mind Graph",   icon: Brain },
        { id: "catch-up", label: "Catch Me Up",  icon: Inbox },
      ],
    },
    {
      label: "PM Controls",
      tabs: [
        { id: "charter",       label: "Charter",        icon: ScrollText },
        { id: "roadmap",       label: "Roadmap",        icon: Map },
        { id: "budget-evm",    label: "Budget / EVM",   icon: DollarSign },
        { id: "wbs",           label: "WBS",            icon: FolderTree },
        { id: "critical-path", label: "Critical Path",  icon: GitMerge },
      ],
    },
    {
      label: "BA Toolkit",
      tabs: [
        { id: "gap-analysis",     label: "Gap Analysis",     icon: ArrowRightLeft },
        { id: "data-dictionary",  label: "Data Dictionary",  icon: Database },
        { id: "use-cases",        label: "Use Cases",        icon: MousePointer2 },
      ],
    },
  ];

  const tabs = tabGroups.flatMap((g) => g.tabs);

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <header className="border-b border-border shrink-0">
        <div className="px-4 lg:px-6 flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/studio")}
              className="gap-1.5"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <div className="h-5 w-px bg-border" />
            <h1 className="font-display font-semibold text-foreground truncate max-w-[200px] sm:max-w-none">
              {project.name}
            </h1>
            <Badge variant="outline" className="text-[10px] px-1.5">
              {project.methodology}
            </Badge>
            {peers.length > 0 && (
              <div className="flex items-center -space-x-2 ml-1">
                {peers.map((peer) => (
                  <Avatar
                    key={peer.user_id}
                    className="h-6 w-6 border-2 border-background"
                    title={`${peer.name || "Someone"}${peer.tab ? ` · viewing ${peer.tab}` : ""}`}
                  >
                    <AvatarFallback
                      className="text-[9px] font-semibold text-white"
                      style={{ backgroundColor: peer.color }}
                    >
                      {(peer.name || "?").slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-border rounded-md overflow-hidden">
              {/* "map" (Flow) hidden for now — per-stage counts weren't rendering clearly, see ProjectFlowMap.tsx */}
              {(["tabs", "crosswalk"] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-2.5 py-1 text-[10px] font-mono transition-colors capitalize flex items-center gap-1 ${
                    viewMode === mode ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground hover:bg-muted/50"
                  }`}
                >
                  {mode === "tabs" ? "Tabs" : "Crosswalk"}
                </button>
              ))}
            </div>
            <Select
              value={project.status}
              onValueChange={(v) => handleStatusChange(v as Project["status"])}
            >
              <SelectTrigger className="w-28 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete project?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </header>

      {/* Body: map or sidebar + content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {viewMode === "map" && (
          <ProjectFlowMap
            project={project}
            activeTab={activeTab}
            onNavigate={(tab) => { setActiveTab(tab); setViewMode("tabs"); }}
          />
        )}
        {viewMode === "crosswalk" && (
          <ProjectCrosswalkView
            activeTab={activeTab}
            onNavigate={(tab) => { setActiveTab(tab); setViewMode("tabs"); }}
          />
        )}
        {/* Vertical Tab Sidebar */}
        <nav className={`w-44 shrink-0 border-r border-border flex flex-col overflow-y-auto py-2 bg-background ${viewMode !== "tabs" ? "hidden" : ""}`} style={{ scrollbarWidth: "thin" }}>
          {tabGroups.map((group, gi) => (
            <div key={group.label} className={gi > 0 ? "mt-3 pt-3 border-t border-border" : ""}>
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                {group.label}
              </p>
              {group.tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs font-body border-l-2 transition-colors text-left ${
                    activeTab === tab.id
                      ? "border-primary text-primary font-semibold bg-orange-50"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <tab.icon className="h-3.5 w-3.5 shrink-0" />
                  {tab.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

      {/* Tab Content */}
      <div className={`flex-1 min-h-0 overflow-hidden flex flex-col ${viewMode !== "tabs" ? "hidden" : ""}`}>
        {project.role === "viewer" && activeTab !== "sharing" && activeTab !== "activity" && (
          <div className="shrink-0 flex items-center gap-2 px-4 py-1.5 text-xs bg-amber-50 text-amber-800 border-b border-amber-200">
            <Lock className="h-3 w-3" /> View only — you have viewer access to this project
          </div>
        )}
        <div className={`flex-1 min-h-0 overflow-hidden ${project.role === "viewer" && activeTab !== "sharing" && activeTab !== "activity" ? "pointer-events-none opacity-60" : ""}`}>
        {activeTab === "diagram" && (
          <div className="flex flex-col h-full">
            <div className="flex flex-col lg:flex-row flex-1 min-h-0">
            {/* Text Panel */}
            <div className="lg:w-[420px] border-b lg:border-b-0 lg:border-r border-border flex flex-col">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
                <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                  Flow Definition
                </span>
                <div className="flex gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHelp(!showHelp)}
                    className="h-7 px-2"
                  >
                    <Lightbulb className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportSVG}
                    className="h-7 px-2 gap-1 text-xs"
                  >
                    <Download className="h-3 w-3" /> SVG
                  </Button>
                  {!text && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLoadExample}
                      className="h-7 text-xs"
                    >
                      Load example
                    </Button>
                  )}
                </div>
              </div>
              {showHelp && (
                <div className="px-4 py-3 bg-secondary/50 border-b border-border text-xs font-mono text-secondary-foreground space-y-1 animate-fade-in shrink-0">
                  <p className="font-body font-semibold text-foreground mb-2">
                    Syntax guide:
                  </p>
                  <p>
                    <span className="text-accent">→</span> or{" "}
                    <span className="text-accent">{"->"}</span> connects nodes
                  </p>
                  <p>
                    <span className="text-accent">[text]</span> = start/end node (pill)
                  </p>
                  <p>
                    <span className="text-accent">{"{text}"}</span> = decision (diamond)
                  </p>
                  <p>
                    <span className="text-accent">(text)</span> = input/output
                  </p>
                  <p>
                    <span className="text-accent">text</span> = process step (box)
                  </p>
                  <p>
                    <span className="text-accent">{'-"label"->'}</span> = labeled edge
                  </p>
                  <p className="pt-1 text-muted-foreground">
                    Each line defines a path. Reuse node names to create branches.
                  </p>
                </div>
              )}
              <Textarea
                value={text}
                onChange={(e) => saveText(e.target.value)}
                placeholder={`[Start] -> Step One -> {Decision?}\n{Decision?} -"yes"-> Result\n{Decision?} -"no"-> Alternative`}
                className="flex-1 border-0 rounded-none resize-none font-mono text-sm focus-visible:ring-0 focus-visible:ring-offset-0 min-h-[200px]"
              />
            </div>

            {/* Diagram Panel */}
            <div className="flex-1 p-3 overflow-auto diagram-area" style={{ backgroundColor: "#0E1420" }}>
              <DiagramCanvas text={text} onChange={saveText} />
            </div>
            </div>
          </div>
        )}

        {activeTab === "stories" && (
          <div className="h-full overflow-auto">
            <UserStoryEditor
              stories={project.userStories || []}
              onChange={(userStories) => {
                setProject((p) => p ? { ...p, userStories } : p);
                debouncedUpdate({ userStories });
              }}
            />
          </div>
        )}

        {activeTab === "kanban" && (
          <div className="h-full overflow-auto">
            <KanbanBoard
              stories={project.userStories || []}
              onChange={(userStories) => {
                setProject((p) => p ? { ...p, userStories } : p);
                debouncedUpdate({ userStories });
              }}
              projectId={project.id}
              myRole={project.role}
              currentUserId={user?.id}
            />
          </div>
        )}

        {activeTab === "timeline" && (
          <div className="h-full overflow-auto">
            <TimelinePanel
              stories={project.userStories || []}
              onChange={(userStories) => {
                setProject((p) => p ? { ...p, userStories } : p);
                debouncedUpdate({ userStories });
              }}
              projectStartDate={project.createdAt}
              sprintLengthWeeks={project.sprintLength || 2}
              onSprintLengthChange={(weeks) => {
                setProject((p) => p ? { ...p, sprintLength: weeks } : p);
                debouncedUpdate({ sprintLength: weeks });
              }}
              milestones={(project as any).milestones || []}
              onMilestonesChange={(milestones) => {
                setProject((p) => p ? { ...p, milestones } as any : p);
                debouncedUpdate({ milestones } as any);
              }}
              baProjectId={baProjectId}
            />
          </div>
        )}

        {activeTab === "velocity" && (
          <div className="h-full overflow-auto">
            <SprintVelocityChart
              stories={project.userStories || []}
              projectStartDate={project.createdAt}
              sprintLengthWeeks={project.sprintLength || 2}
            />
          </div>
        )}

        {activeTab === "analysis" && (
          <div className="h-full overflow-auto">
            <AnalysisPanel
              analyses={project.analyses || []}
              onChange={(analyses) => {
                setProject((p) => p ? { ...p, analyses } : p);
                debouncedUpdate({ analyses });
              }}
            />
          </div>
        )}

        {activeTab === "documents" && (
          <div className="h-full">
            <DocumentEditor
              documents={project.documents || []}
              onChange={(documents) => {
                setProject((p) => p ? { ...p, documents } : p);
                debouncedUpdate({ documents });
              }}
              projectId={project.id}
              myRole={project.role}
              currentUserId={user?.id}
            />
          </div>
        )}

        {activeTab === "requirements" && (
          <div className="h-full overflow-auto">
            <RequirementsPanel
              mongoProjectId={project.id}
              projectName={project.name}
              team={team}
              myRole={project.role}
              onStoriesGenerated={(newStories) => {
                const merged = [...(project.userStories || []), ...newStories];
                setProject((p) => p ? { ...p, userStories: merged } : p);
                updateProject(project.id, { userStories: merged });
                setActiveTab("stories");
              }}
              onAnalysisGenerated={(newAnalysis) => {
                const analyses = [...(project.analyses || []), newAnalysis];
                setProject((p) => p ? { ...p, analyses } : p);
                updateProject(project.id, { analyses });
                setActiveTab("analysis");
              }}
              onBaProjectReady={(pid) => setBaProjectId(pid)}
            />
          </div>
        )}

        {activeTab === "compliance" && (
          <div className="h-full overflow-auto">
            <CompliancePanel mongoProjectId={project.id} />
          </div>
        )}

        {activeTab === "agent-log" && (
          <div className="h-full overflow-auto">
            <AgentLogPanel mongoProjectId={project.id} />
          </div>
        )}

        {activeTab === "knowledge-base" && (
          <div className="h-full overflow-auto">
            <KnowledgeBasePanel mongoProjectId={project.id} projectName={project.name} />
          </div>
        )}

        {activeTab === "team" && (
          <div className="h-full overflow-auto">
            <TeamPanel
              projectId={project.id}
              team={team}
              onChange={(updated) => {
                setTeam(updated);
                updateProject(project.id, { team: updated } as any);
              }}
            />
          </div>
        )}

        {activeTab === "sharing" && (
          <div className="h-full overflow-auto">
            <ProjectMembersPanel
              projectId={project.id}
              myRole={project.role}
              currentUserId={user?.id}
            />
          </div>
        )}

        {activeTab === "activity" && (
          <div className="h-full overflow-auto">
            <ActivityFeedPanel projectId={project.id} currentUserId={user?.id} />
          </div>
        )}

        {activeTab === "schedule" && baProjectId && (
          <div className="h-full overflow-auto">
            <SchedulePanel
              projectId={project.id}
              baProjectId={baProjectId}
              team={team}
            />
          </div>
        )}

        {activeTab === "test-cases" && baProjectId && (
          <div className="h-full overflow-auto">
            <TestCasesPanel baProjectId={baProjectId} />
          </div>
        )}
        {activeTab === "test-cases" && !baProjectId && (
          <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
            Visit the Requirements tab first to initialise the project backend.
          </div>
        )}

        {activeTab === "test-runner" && (
          <div className="h-full overflow-auto">
            <TestRunnerPanel mongoProjectId={project.id} baProjectId={baProjectId} />
          </div>
        )}

        {activeTab === "impact-analysis" && (
          <div className="h-full overflow-auto">
            <ImpactAnalysisPanel
              mongoProjectId={project.id}
              baProjectId={baProjectId}
              onAnalysisGenerated={(newAnalysis) => {
                const analyses = [...(project.analyses || []), newAnalysis];
                setProject((p) => p ? { ...p, analyses } : p);
                updateProject(project.id, { analyses });
                setActiveTab("analysis");
              }}
            />
          </div>
        )}

        {activeTab === "case-studies" && (
          <div className="h-full overflow-auto">
            <CaseStudiesPanel baProjectId={baProjectId} />
          </div>
        )}

        {activeTab === "genius-labs" && (
          <div className="h-full overflow-hidden">
            <GeniusLabsPanel projectId={project.id} />
          </div>
        )}

        {activeTab === "handover" && (
          <div className="h-full overflow-hidden">
            <HandoverPanel
              projectId={project.id}
              baProjectId={baProjectId}
              onStoriesAdded={(newStories) => {
                const merged = [...(project.userStories || []), ...newStories];
                setProject((p) => p ? { ...p, userStories: merged } : p);
                updateProject(project.id, { userStories: merged });
              }}
              onStakeholdersAdded={(newStakeholders) => {
                const merged = [...((project as any).stakeholders || []), ...newStakeholders];
                setProject((p) => p ? { ...p, stakeholders: merged } as any : p);
                updateProject(project.id, { stakeholders: merged } as any);
              }}
            />
          </div>
        )}

        {activeTab === "job-intelligence" && (
          <div className="h-full overflow-hidden">
            <JobIntelligencePanel projectId={project.id} />
          </div>
        )}

        {activeTab === "evolution-log" && baProjectId && (
          <div className="h-full overflow-auto">
            <EvolutionLogPanel baProjectId={baProjectId} />
          </div>
        )}

        {activeTab === "stakeholders" && (
          <div className="h-full overflow-auto">
            <StakeholderPanel
              stakeholders={project.stakeholders || []}
              onChange={(stakeholders) => {
                setProject((p) => p ? { ...p, stakeholders } : p);
                debouncedUpdate({ stakeholders });
              }}
              raciMatrix={project.raciMatrix || []}
              onRaciChange={(raciMatrix) => {
                setProject((p) => p ? { ...p, raciMatrix } : p);
                debouncedUpdate({ raciMatrix });
              }}
            />
          </div>
        )}

        {activeTab === "traceability" && (
          <div className="h-full overflow-auto">
            <TraceabilityMatrix baProjectId={baProjectId} />
          </div>
        )}

        {activeTab === "capa" && (
          <div className="h-full overflow-auto">
            <CAPAPanel
              baProjectId={baProjectId}
              projectId={project.id}
              myRole={project.role}
              currentUserId={user?.id}
              requirements={(project.userStories || []).map((s: any) => ({ id: s.id, title: s.title }))}
            />
          </div>
        )}

        {activeTab === "suppliers" && (
          <div className="h-full overflow-auto">
            <SupplierPanel baProjectId={baProjectId} />
          </div>
        )}

        {activeTab === "decisions" && (
          <div className="h-full overflow-auto">
            <DecisionLogPanel baProjectId={baProjectId} />
          </div>
        )}

        {activeTab === "risks" && (
          <div className="h-full overflow-auto">
            <RiskRegisterPanel baProjectId={baProjectId} />
          </div>
        )}

        {activeTab === "meeting-notes" && (
          <div className="h-full overflow-auto">
            <MeetingNotesPanel baProjectId={baProjectId} stakeholders={project?.stakeholders || []} />
          </div>
        )}

        {activeTab === "sprints" && (
          <div className="h-full overflow-auto">
            <SprintPlanningPanel baProjectId={baProjectId} />
          </div>
        )}

        {activeTab === "okr" && (
          <div className="h-full overflow-auto">
            <OKRPanel baProjectId={baProjectId} userStories={project?.userStories || []} />
          </div>
        )}

        {activeTab === "brain" && (
          <div className="h-full overflow-auto">
            <BrainTrajectoryPanel baProjectId={baProjectId} />
          </div>
        )}

        {activeTab === "catch-up" && (
          <div className="h-full overflow-hidden">
            <CatchMeUpPanel baProjectId={baProjectId} stakeholders={project?.stakeholders || []} />
          </div>
        )}
        {activeTab === "roadmap" && (
          <div className="h-full overflow-hidden">
            <RoadmapPanel
              stories={project.userStories || []}
              onChange={(userStories) => {
                setProject((p) => p ? { ...p, userStories } : p);
                debouncedUpdate({ userStories });
              }}
            />
          </div>
        )}
        {activeTab === "charter" && (
          <div className="h-full overflow-hidden">
            <ProjectCharterPanel />
          </div>
        )}
        {activeTab === "budget-evm" && (
          <div className="h-full overflow-hidden">
            <BudgetEVMPanel />
          </div>
        )}
        {activeTab === "wbs" && (
          <div className="h-full overflow-hidden">
            <WBSPanel />
          </div>
        )}
        {activeTab === "critical-path" && (
          <div className="h-full overflow-hidden">
            <CriticalPathPanel stories={project?.userStories || []} />
          </div>
        )}
        {activeTab === "gap-analysis" && (
          <div className="h-full overflow-hidden">
            <GapAnalysisPanel />
          </div>
        )}
        {activeTab === "data-dictionary" && (
          <div className="h-full overflow-hidden">
            <DataDictionaryPanel />
          </div>
        )}
        {activeTab === "use-cases" && (
          <div className="h-full overflow-hidden">
            <UseCasePanel />
          </div>
        )}
        </div>
      </div>
      </div>
    </div>
  );
}
